import { makeStyles } from '@mui/styles';
import Autocomplete from 'apollo-react/components/Autocomplete';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { removeErrorByRowId } from '../../../actions/ErrorActions';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getAppSettingsByKey } from '../../../selectors/AppConfigsSelectors';
import { getUITA, isFalse, isTrue } from '../../../utils/commonUtils';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import { autoCompleteControlValue, getAutoCompleteOptions } from '../../actions/runtime.actions';
import RequiredIndicator from '../../components/RequiredIndicator';
import {
  AUTOCOMPLETE_NO_OPTIONS_TEXT,
  AUTOCOMPLETE_SEARCH_DELAY,
  FCM_HIDDEN_CONTROL_STYLES,
} from '../../constants/runtime.constants';
import { getHelperError } from '../../selectors/form-manager.selectors';
import { getRowIdFromEntityData } from '../../selectors/ssGrid.selectors';
import { showAsteriskBefore } from '../../utils/form-manager.utils';

/**
 * When we select a value from options, onChange is fired first and onBlur is fired after.
 * This causes the selected value to be overridden by the value entered in the field.
 * We need to prevent firing onBlur when the value is selected from options
 */
const handleChangeHelper = (isValueSelectedFromOptions) => {
  isValueSelectedFromOptions.current = true;

  setTimeout(() => {
    isValueSelectedFromOptions.current = false;
  }, 50);
};

const useStyles = makeStyles({
  root: {
    paddingTop: ({ hiddenLabel }) => (hiddenLabel ? 16 : 14),
    paddingBottom: 8,
  },
  disabled: {
    '& .MuiInputBase-root.Mui-disabled': {
      border: '1px solid #d9d9d9',
    },
  },
  autoCompleteTextBoxInTable: {
    display: 'flex',
    '& > div': {
      display: 'flex',
      width: '100%',
    },
  },
  hiddenControl: FCM_HIDDEN_CONTROL_STYLES,
  disabledWithError: {
    '& .MuiInputBase-root.Mui-disabled': {
      '& fieldset': {
        border: '2px solid #bd0100 !important',
      },
    },
  },
});

const AutoCompleteTextBox = ({
  label,
  actions,
  className,
  isRequired,
  isDisabledWithZoom,
  value = '',
  error,
  inTable,
  isEditor,
  inFieldGroup,
  isHiddenLabel,
  entityName,
  fieldName,
  instanceId,
  formKey,
  setIsAutoCompleteLoading,
  enforceSelection,
  disabled,
  isOnline,
  index,
  isControlHiddenFCM,
}) => {
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const rowId = useFormSelector(getRowIdFromEntityData, { entityName, index });
  const [localValue, setLocalValue] = useState({ label: value, value });
  const [currentValue, setCurrentValue] = useState('');
  const [options, setOptions] = useState([]);
  const [noOptionsText, setNoOptionsText] = useState(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
  const timeoutRef = useRef(null);
  const isValueSelectedFromOptions = useRef(false);
  const inputRef = useRef(null);
  const hiddenLabel = inTable || (inFieldGroup && isHiddenLabel);
  const classes = useStyles({ hiddenLabel });
  const helperError = useFormSelector(getHelperError, { entityName, fieldName, index });
  const useContainsOperatorSetting = useSelector(
    getAppSettingsByKey('TypeAhead/UseContainsOperator')
  );
  const useContainsOperator = isTrue(useContainsOperatorSetting);
  const noAutocompletionText = translateString('No autocompletion offline');
  const helperText = isOnline ? helperError?.message : noAutocompletionText;

  useEffect(() => {
    // Update inner state when `value` is changed from outside.
    setLocalValue({ label: value, value });

    if (isNilOrEmpty(value)) {
      setOptions([]);
    }
  }, [value]);

  useEffect(() => {
    setNoOptionsText(
      !isOnline
        ? AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS
        : AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH
    );
  }, [isOnline]);

  useLayoutEffect(() => {
    inputRef.current?.querySelector('input')?.setAttribute('aria-label', label ?? fieldName);
  }, [label, fieldName]);

  const handleChange = (_, newValue, reason) => {
    if (!newValue) {
      setNoOptionsText(
        !isOnline
          ? AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS
          : AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH
      );
      setOptions([]);

      // clearing the value is handled by handleBlur
      return;
    }

    if (reason === 'selectOption') {
      handleChangeHelper(isValueSelectedFromOptions);
    }

    if (newValue.custom) {
      dispatch(
        autoCompleteControlValue({
          value: newValue.label,
          instanceId,
          label,
          formKey,
          rowId,
          custom: true,
        })
      );
    } else {
      if (localValue.value !== newValue.value) {
        setLocalValue(newValue);
        actions.bulkUpdateControlValues(newValue.changesToApply);
      }
    }
    setOptions([]);
  };

  const handleInputChange = (_, newValue, reason) => {
    setCurrentValue(newValue);
    clearTimeout(timeoutRef.current);

    if (!isNilOrEmpty(helperError)) {
      dispatch(removeErrorByRowId(helperError.rowID));
    }

    if (reason !== 'input' || isNilOrEmpty(newValue) || !isOnline) {
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      setIsAutoCompleteLoading(true);

      const newOptions = await dispatch(
        getAutoCompleteOptions(newValue, entityName, fieldName, instanceId, formKey)
      );

      setIsAutoCompleteLoading(false);
      setOptions(newOptions);

      if (!newOptions.length && noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH) {
        setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS);
      } else if (newOptions.length && noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS) {
        setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
      }
    }, AUTOCOMPLETE_SEARCH_DELAY);
  };

  const handleBlur = async ({ target }) => {
    if (noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS && isOnline) {
      setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
    }

    if (isValueSelectedFromOptions.current) {
      return;
    }

    const newValue = target.value.trim();

    if (isNilOrEmpty(newValue) && isNilOrEmpty(value)) {
      return;
    }

    if (newValue !== value) {
      batch(() => {
        actions.updateControlValue(newValue); // TODO: comment this and run tests
        isOnline &&
          dispatch(
            autoCompleteControlValue({
              value: newValue,
              instanceId,
              label,
              formKey,
              rowId,
            })
          );
      });
      setOptions([]);
    }
  };

  return (
    <div
      className={classNames(
        classes.root,
        isControlHiddenFCM ? classes.hiddenControl : '',
        isEditor && classes.disabled,
        error && isDisabledWithZoom ? classes.disabledWithError : ''
      )}
      data-hidden-fcm={isControlHiddenFCM}
      ui-ta={getUITA('FormCtrl', 'SSTextBox', entityName, fieldName)}
    >
      <span className={isRequired && hiddenLabel ? classes.autoCompleteTextBoxInTable : ''}>
        {isRequired && hiddenLabel && <RequiredIndicator />}
        <Autocomplete
          ref={inputRef}
          style={{
            minWidth: inTable && !isEditor ? 184 : 0,
            marginTop: hiddenLabel ? 5 : 0,
          }}
          className={className || undefined}
          id={instanceId}
          label={hiddenLabel ? '' : label}
          forcePopupIcon={false}
          asteriskPosition={showAsteriskBefore(inTable ? '' : label, inTable) ? 'before' : 'after'}
          fullWidth
          noOptionsText={translateString(noOptionsText)}
          getOptionLabel={(option) =>
            (option.value === localValue.value ? localValue.value : option.label) ?? ''
          }
          matchFrom={useContainsOperator ? 'any' : 'start'}
          filterSelectedOptions={false}
          required={isRequired}
          error={error || (helperText !== undefined && helperText !== noAutocompletionText)}
          helperText={helperText}
          disabled={isEditor || isDisabledWithZoom || disabled}
          clearOnBlur={false}
          source={options}
          value={localValue}
          TextFieldProps={{
            title: isRequired && !value ? translateString('Please fill out this field.') : '',
          }}
          allowAddNewOptions={isFalse(enforceSelection) && currentValue}
          addLabelFunc={(inputValue) => `${translateString('Add')} "${inputValue}"`}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onBlur={handleBlur}
          showClearIndicator={false}
        />
      </span>
    </div>
  );
};

export default AutoCompleteTextBox;
